import { faCircleArrowUp } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, CardContent, Stack, TextField, Typography } from "@mui/material";
import { VK_ENTER } from "app/appConstants";
import { MprcCard, MprcChipChoice, MprcClickable, MprcInfiniteLoading, MprcLink } from "components";
import { IconPreview } from "components/icons/IconPreview";
import { MprcNotice } from "components/MprcNotice";
import { MprcRequestSnackbarErrorReport } from "components/MprcRequestErrorReport";
import { KnowledgeContextualRAGResponse, RAGKnowledgeSource } from "generated/CoreApi_v1";
import { useSystemUsages } from "hooks/useSystemUsages";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import { KnowledgePaths } from "routes/KnowledgeRouter";

/////////////////////////////////////////////////////////////////////////////////////////////////

export const RagSearch = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [viewData, setViewData] = useState<KnowledgeContextualRAGResponse[]>([]);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const newQuestionRef = useRef<HTMLInputElement>(null);

  const { t } = useTranslation("shared");
  const { enqueueSnackbar } = useSnackbar();

  const navigate = useNavigate();
  const location = useLocation();
  const api = useCoreApi();

  const conversationId = useRef<string | null>(null);
  const canContinueSearch = useRef<boolean>(false);

  const { checkLimit, isChecking, limits } = useSystemUsages();

  ///
  const ragSearch = async (question: string) => {
    setLoading(true);
    try {

      const response = await api.v1.globalSearch.ragSearch({
        searchText: question,
        conversationId: conversationId.current
      });

      if (conversationId.current != null && conversationId.current != response.data.conversationId) {
        setViewData([response.data]);
        enqueueSnackbar(t("GlobalSearch.RagSearch.ConversationExpiredText"), { variant: "warning" });
      }
      else {
        setViewData(state => {
          const newData = (state ?? []).filter(p => p.turnNumber != -1);
          return [...newData, response.data];
        });
      }

      canContinueSearch.current = response.data.errorCode == null || response.data.errorCode > -2;
      conversationId.current = response.data.conversationId;
    }
    catch (e) {
      MprcRequestSnackbarErrorReport(e);
    }
    finally {
      setLoading(false);
    }
  };

  ///

  const handleNewSearch = (newQuestion: string | null) => {
    canContinueSearch.current = true;
    conversationId.current = null;
    setViewData([]);
    if (newQuestion != null) newQuestionInContext(newQuestion);
  };

  ///

  const handleNewQuestionInContext = () => {
    if (newQuestionRef.current) {
      const question = newQuestionRef.current.value.trim();
      if (question != "") {
        if (canContinueSearch.current) newQuestionInContext(question);
        else handleNewSearch(question);

        newQuestionRef.current.value = "";
      }
    }
  };

  ///

  const newQuestionInContext = (question: string) => {
    setViewData(state => ([...state ?? [], {
      question,
      answer: "",
      sourcesUsed: [],
      relatedSearchSuggestions: [],
      contextUsed: "",
      processingTime: "",
      error: null,
      errorCode: null,
      metadata: null!,
      conversationId: "",
      turnNumber: -1,
      outOfContext: false,
      subjectMismatch: false,
      contextRelevance: -1,
      isFirstQuestion: false,
      conversationSummary: null,
      contextualMetadata: null!
    }]));

    ragSearch(question);
  };

  ///

  const goPublication = (item: RAGKnowledgeSource) => {
    var url = KnowledgePaths.PublicationViewerResolver(item.publicationId, item.publicationType);
    linkNavigate(url);
  };

  ///

  const linkNavigate = (url: string) => {
    window.sessionStorage.setItem("LastRagSearch", JSON.stringify(viewData));
    navigate(document.location.search, { replace: true, state: { hasPreviousSearch: true } });
    navigate(url);
  };

  ///

  const restoreConversation = () => {
    var data = window.sessionStorage.getItem("LastRagSearch");
    if (data != null) {
      setViewData(JSON.parse(data));
      window.sessionStorage.removeItem("LastRagSearch");
    }
  };

  ///

  const RenderQuestion = (question: string) => {
    return (
      <Stack direction="row" justifyContent="end">
        <MprcCard paperColor="notice" elevation={0} sx={{ p: 1, border: 1, borderColor: "#fff2cc" }}>
          <Typography color="black">
            {question}
          </Typography>
        </MprcCard>
      </Stack>
    );
  };

  ///

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  ///

  const RenderNewConversation = () => {
    return (
      <Stack direction="column" gap={1} justifyContent="center" alignItems="center" height="100%">
        <img src="/img/app/RagSearch-NewSearch.gif" width={272} height={270}></img>
      </Stack>
    );
  };

  ///

  const RenderConversation = () => {
    if (viewData == null) return null;

    return (
      <Box className="ragsearch">
        {
          viewData.map((item, i) => {
            return (
              <Box id={`answer-{i}`} key={`answer-{i}`} mt={2}>

                {RenderQuestion(item.question)}

                {
                  item.turnNumber > -1 &&
                  <Box mt={1}>
                    {item.error
                      ? (
                        <MprcCard paperColor="error" elevation={1}>
                          <CardContent>
                            <Typography color="white">{item.error}</Typography>
                          </CardContent>
                        </MprcCard>
                      )
                      : (
                        <>
                          <div
                            className={item.outOfContext ? "subjectMismatch" : undefined}
                            dangerouslySetInnerHTML={{ __html: item.answer }}>
                          </div>

                          {!item.outOfContext &&
                            <Stack direction="row" gap={1} flexWrap="wrap">
                              {item.sourcesUsed.map((s, si) => (<MprcChipChoice key={si} icon={<IconPreview color="inherit" style={{ paddingLeft: "4px", fontSize: "11px" }} />} title={t("GlobalSearch.RagSearch.PostTooltip")} label={s.title} onClick={() => goPublication(s)} />))}
                              {item.relatedSearchSuggestions.map((s, si) => (<MprcChipChoice key={si} label={s} title={s} onClick={() => newQuestionInContext(s)} />))}
                            </Stack>
                          }
                        </>
                      )
                    }
                  </Box>
                }
              </Box >
            );
          })
        }
      </Box>
    );
  };

  ///

  useEffect(() => {
    if (location.state?.hasPreviousSearch != undefined) restoreConversation();
    else if (location.state?.searchText != null) handleNewSearch(location.state.searchText);

    // Remove "estado" anterior
    if (location.state) navigate(document.location.search, { replace: true, state: undefined });
  }, [location]);

  useEffect(() => {
    scrollToBottom();
    newQuestionRef.current?.focus();
  });

  useEffect(() => {
    checkLimit();
  }, []);

  ///
  return isChecking
    ? <MprcInfiniteLoading visible />
    : limits?.iaRequestsExceeded == true
      ? <MprcNotice items={[{ text: t("GlobalSearch.RagSearch.RequestExceededMessage") }]} />
      : (
        <Stack direction="column"
          overflow="hidden"
          height="calc(100% - 32px)"
          padding={2}
          borderRadius={2}
          sx={{ backgroundColor: "#fff" }}
          boxShadow={"0px 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12)"}
        >
          <Stack direction="column" height="100%">
            <Box id="conversation-area" flex={1} sx={{ overflowY: "auto" }}>

              {viewData.length == 0 &&
                RenderNewConversation()
              }

              {RenderConversation()}

              {loading && <img src="/img/app/RagSearch-Typing.gif" width={128} height={64}></img>}

              <div ref={messagesEndRef} />
            </Box>
            <Box id="question-area" flex={0} display={loading && viewData && viewData.length == 1 ? "none" : ""}>
              <Stack direction="column" gap={.5} mt={2} alignItems={"end"}>
                {
                  (conversationId.current == null || (viewData.length != 0 && viewData[0].outOfContext == false)) &&
                  <TextField
                    fullWidth
                    inputRef={newQuestionRef}
                    hiddenLabel
                    variant="outlined"
                    autoComplete="off"
                    autoCorrect="off"
                    placeholder={conversationId.current == null ? t("GlobalSearch.RagSearch.NewQuestionPlaceholder") : t("GlobalSearch.RagSearch.ContinueSearchPlaceholder")}
                    onKeyDown={(e) => e.key == VK_ENTER && handleNewQuestionInContext()}
                    slotProps={
                      {
                        htmlInput: {
                          sx: { p: 1 }
                        },
                        input: {
                          endAdornment: <MprcClickable onClick={handleNewQuestionInContext}><FontAwesomeIcon icon={faCircleArrowUp} /></MprcClickable>
                        }
                      }
                    }
                  />
                }
                {
                  viewData.length != 0 &&
                  <MprcLink onClick={() => handleNewSearch(null)}>
                    {t("GlobalSearch.RagSearch.NewQuestion")}
                  </MprcLink>
                }
              </Stack>
            </Box>
          </Stack>
        </Stack>
      );
};